import { Navigate, useSearchParams } from 'react-router';
import { PAID_TIERS, type PaymentTier } from '@mondy/shared';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { UpgradeOptionsInline } from '@/components/UpgradeModal';
import { Spinner } from '@/components/Spinner';

/**
 * Standalone landing page for the upgrade flow. The native app deep-links
 * here (Linking.openURL('https://mondy.app/upgrade?tier=silver')); web users
 * also use this page when they want to browse plans without first hitting a
 * 402 from a group action.
 *
 * If unauthed: bounce to /login?next=/upgrade?tier=X so the redirect target
 * (and pre-selected tier) survives the login round-trip.
 */
export function UpgradePage() {
  const me = useCurrentUser();
  const [params] = useSearchParams();
  const rawTier = params.get('tier');
  const preselected: PaymentTier | null =
    rawTier && (PAID_TIERS as readonly string[]).includes(rawTier)
      ? (rawTier as PaymentTier)
      : null;

  if (me === undefined) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center">
        <Spinner size="md" />
      </main>
    );
  }

  if (me === null) {
    const next = preselected ? `/upgrade?tier=${preselected}` : '/upgrade';
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-6 px-6 py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Upgrade your plan</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          One-time payment, no subscription. Pick a tier to unlock more groups
          and members.
        </p>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Current plan:{' '}
          <span className="font-semibold text-[color:var(--color-fg)]">
            {me.paymentTier.charAt(0).toUpperCase()}
            {me.paymentTier.slice(1)}
          </span>
        </p>
      </header>
      <UpgradeOptionsInline preselectedTier={preselected} />
    </main>
  );
}
